import { useState } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

interface UploadResult {
  success: boolean;
  uploaded: Record<string, string>;
  errors: string[];
}

export default function UploadImagesPage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/upload-default-images`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: UploadResult = await res.json();
      setResult(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const uploadedCount = result ? Object.keys(result.uploaded).length : 0;

  return (
    <AdminLayout>
      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">圖片上傳至 Supabase Storage</h1>
        <p className="text-gray-500 text-sm mb-8">
          點擊下方按鈕，將所有網站預設圖片批次上傳至你的 Supabase Storage，完成後圖片網址將不再含有 readdy.ai。
        </p>

        <button
          onClick={handleUpload}
          disabled={loading}
          className="px-8 py-3.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors flex items-center gap-3"
        >
          {loading && <i className="ri-loader-4-line animate-spin" />}
          {loading ? '上傳中，請稍候（約 30–60 秒）...' : '開始批次上傳所有圖片'}
        </button>

        {error && (
          <div className="mt-6 bg-red-50 border border-red-100 rounded-xl p-4 text-red-600 text-sm">
            發生錯誤：{error}
          </div>
        )}

        {result && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-xl">
                <i className="ri-check-line text-green-600" />
              </div>
              <div>
                <div className="font-bold text-gray-900">上傳完成！</div>
                <div className="text-sm text-gray-500">
                  成功上傳 {uploadedCount} 張圖片
                  {result.errors.length > 0 && `，${result.errors.length} 張失敗`}
                </div>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4">
                <div className="text-sm font-semibold text-yellow-700 mb-2">失敗項目：</div>
                {result.errors.map((e, i) => (
                  <div key={i} className="text-xs text-yellow-600 font-mono">{e}</div>
                ))}
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-700 mb-3">已上傳圖片 URL 對照表：</div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {Object.entries(result.uploaded).map(([key, url]) => (
                  <div key={key} className="flex items-start gap-3 text-xs">
                    <span className="font-mono text-orange-500 whitespace-nowrap flex-shrink-0 pt-0.5">{key}</span>
                    <span className="text-gray-500 break-all">{url}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
              <i className="ri-information-line mr-1" />
              圖片已上傳完成！請截圖或複製上方 URL，然後告訴 AI「圖片已上傳完成，請更新程式碼」，AI 會自動把所有頁面的圖片網址換成 Supabase Storage 的網址。
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
